import { useState, useEffect } from 'react';
import {
  AutoparkService,
  hasDriverData,
  IAutoparkCar,
  IAutoparkDriverWithProfileAndCars,
  IRegisteredAutoparkDriver,
} from '../../services/autopark.service';
import Pagination from '../Pagination/Pagination';
import ConfirmationModal from '../Modal/ConfirmationModal';
import EditDriverModal from '../Modal/EditDriverModal';
import AddDriverModal from '../Modal/AddDriverModal';
import { useAppSelector } from '../../store/hooks';
import './autoparkDrivers.css';
import { formatPhone } from '../../utils/phone';
import { fullName } from '../../utils/utils';
import { formatBackendDate } from '../../utils/date.utils';

interface AutoparkDriversProps {
  autoparkId: string;
}

function AutoparkDrivers({ autoparkId }: AutoparkDriversProps) {
  const [drivers, setDrivers] = useState<IRegisteredAutoparkDriver[]>([]);
  const user = useAppSelector((state) => state.user.user);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedDrivers, setExpandedDrivers] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [autoparkName, setAutoparkName] = useState<string>('ЭПЛ');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState<string | null>(null);
  const [driverProfileId, setDriverProfileId] = useState<string | null>(null);
  // Состояние для модального окна редактирования
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [driverToEdit, setDriverToEdit] = useState<IRegisteredAutoparkDriver | null>(null);

  // Состояние для модального окна добавления водителя
  const [isAddDriverModalOpen, setIsAddDriverModalOpen] = useState(false);

  const limit = 10;

  // Функция для загрузки водителей автопарка
  const fetchDrivers = async () => {
    if (!autoparkId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await AutoparkService.getAutoparkDrivers(autoparkId, currentPage, limit);
      // Без данных водителя строку не показать и не изменить
      setDrivers(response.data.filter(hasDriverData));
      setTotalPages(response.totalPages);
      setTotalCount(response.totalCount);

      // Получаем название автопарка
      try {
        const autoparkData = await AutoparkService.getAutoparkById(autoparkId);
        setAutoparkName(autoparkData.tradeName || autoparkData.legalName || 'ЭПЛ');
      } catch (err) {
        // Если не удалось получить название, используем дефолтное
        setAutoparkName('ЭПЛ');
      }
    } catch (err: any) {
      setError(err.message || 'Ошибка при загрузке водителей');
      console.error('Ошибка при загрузке водителей:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, [autoparkId, currentPage]);

  const toggleDriverExpansion = (driverId: string) => {
    const newExpanded = new Set(expandedDrivers);
    if (newExpanded.has(driverId)) {
      newExpanded.delete(driverId);
    } else {
      newExpanded.add(driverId);
    }
    setExpandedDrivers(newExpanded);
  };

  const handleDeleteDriver = (driverId: string, driverProfileId: string) => {
    setDriverToDelete(driverId);
    setDriverProfileId(driverProfileId);
    setIsModalOpen(true);
  };

  const confirmDeleteDriver = async () => {
    if (!driverToDelete || !autoparkId || !driverProfileId) return;

    try {
      // Вызываем API для удаления водителя из автопарка
      await AutoparkService.removeDriverFromAutopark(driverToDelete, autoparkId);

      // Дополнительно отвязываем профиль от автопарка
      await AutoparkService.unlinkProfileFromAutopark(autoparkId, driverProfileId);

      // Удаляем водителя из локального состояния
      setDrivers((prev) => (prev ? prev.filter((driver) => driver.driver.id !== driverToDelete) : []));
      setTotalCount((prev) => prev - 1);

      // Закрываем модальное окно
      setIsModalOpen(false);
      setDriverToDelete(null);
      fetchDrivers();
    } catch (error: any) {
      console.error('Ошибка при удалении водителя:', error);
      setError(error.message || 'Ошибка при удалении водителя');
    }
  };

  const cancelDeleteDriver = () => {
    setIsModalOpen(false);
    setDriverToDelete(null);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleEditDriver = (driverData: IRegisteredAutoparkDriver) => {
    setDriverToEdit(driverData);
    setIsEditModalOpen(true);
  };

  const handleEditSuccess = async () => {
    // Перезагружаем список водителей после успешного редактирования
    await fetchDrivers();
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setDriverToEdit(null);
  };

  const handleAddDriverClick = () => {
    setIsAddDriverModalOpen(true);
  };

  const handleCloseAddDriverModal = () => {
    setIsAddDriverModalOpen(false);
  };

  const handleAddDriverSuccess = async () => {
    // Перезагружаем список водителей после успешного добавления
    await fetchDrivers();
  };

  // Обработка состояний загрузки и ошибок
  if (loading) {
    return (
      <div className="autopark-drivers">
        <div className="autopark-drivers__loading">Загрузка водителей...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="autopark-drivers">
        <div className="autopark-drivers__error">
          <p>Ошибка: {error}</p>
          <button onClick={fetchDrivers} className="retry-button">
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="autopark-drivers">
      {/* Заголовок */}
      <div className="drivers-header">
        <h2 className="drivers-title">Водители автопарка "{autoparkName}"</h2>
        {user?.role === 'autopark_employee' && (
          <button className="add-car-button" onClick={handleAddDriverClick}>
            Добавить
          </button>
        )}
      </div>

      {/* Заголовок таблицы */}
      <div className="drivers-table-header">
        <div className="drivers-table-header__cell drivers-table-header__cell--name">
          <span>ФИО водителя</span>
          {/* <svg className="sort-icon sort-icon--active" width="20" height="20" viewBox="0 0 19 13" fill="none">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M0 0.75C0 0.335786 0.335786 0 0.75 0H17.75C18.1642 0 18.5 0.335786 18.5 0.75C18.5 1.16421 18.1642 1.5 17.75 1.5H0.75C0.335786 1.5 0 1.16421 0 0.75ZM14.7197 3.21967C15.0126 2.92678 15.4874 2.92678 15.7803 3.21967L18.2803 5.71967C18.5732 6.01256 18.5732 6.48744 18.2803 6.78033C17.9874 7.07322 17.5126 7.07322 17.2197 6.78033L16 5.56066V11.75C16 12.1642 15.6642 12.5 15.25 12.5C14.8358 12.5 14.5 12.1642 14.5 11.75V5.56066L13.2803 6.78033C12.9874 7.07322 12.5126 7.07322 12.2197 6.78033C11.9268 6.48744 11.9268 6.01256 12.2197 5.71967L14.7197 3.21967ZM0 5.75C0 5.33579 0.335786 5 0.75 5H8.75C9.16421 5 9.5 5.33579 9.5 5.75C9.5 6.16421 9.16421 6.5 8.75 6.5H0.75C0.335786 6.5 0 6.16421 0 5.75ZM0 10.75C0 10.3358 0.335786 10 0.75 10H9.75C10.1642 10 10.5 10.3358 10.5 10.75C10.5 11.1642 10.1642 11.5 9.75 11.5H0.75C0.335786 11.5 0 11.1642 0 10.75Z"
              fill="#1D61E7"
            />
          </svg> */}
        </div>
        <div className="drivers-table-header__cell drivers-table-header__cell--cars">
          <span>Гос номер автомобиля</span>
        </div>
        <div className="drivers-table-header__cell">
          <span>Номер телефона</span>
        </div>
        <div className="drivers-table-header__cell">
          <span>Внести изменения</span>
        </div>
      </div>

      {/* Содержимое таблицы */}
      <div className="drivers-table-content">
        {loading ? (
          <div className="loading-state">Загрузка водителей...</div>
        ) : error ? (
          <div className="error-state">{error}</div>
        ) : drivers.length === 0 ? (
          <div className="loading-state">Водители не найдены</div>
        ) : (
          drivers.map((driverData) => (
            <div key={driverData.driver.id} className="drivers-table-row">
              <div className="drivers-table-row__cell drivers-table-row__cell--name">
                <span className="driver-name">{fullName(driverData.profile)}</span>
                {driverData?.cars?.length > 0 && (
                  <button className="expand-button" onClick={() => toggleDriverExpansion(driverData.driver.id)}>
                    <svg
                      className={`expand-icon ${expandedDrivers.has(driverData.driver.id) ? 'expand-icon--rotated' : ''}`}
                      width="14"
                      height="8"
                      viewBox="0 0 9 5"
                      fill="none"
                    >
                      <path
                        d="M4.2994 4.5705L8.05006 0.702724C8.28409 0.461381 8.14223 0 7.83399 0H0.332677C0.0244348 0 -0.117429 0.46138 0.116606 0.702723L3.86726 4.5705C3.9916 4.69872 4.17507 4.69872 4.2994 4.5705Z"
                        fill="#3563E9"
                      />
                    </svg>
                  </button>
                )}
              </div>

              <div className="drivers-table-row__cell drivers-table-row__cell--cars">
                {expandedDrivers.has(driverData.driver.id) && driverData.cars.length > 0 ? (
                  <div className="cars-list">
                    {driverData.cars.map((car: IAutoparkCar, index: number) => (
                      <div key={car.id} className="car-item">
                        <span className="car-number">{index + 1}.</span>
                        <span className="car-plate">{car.number}</span>
                        <span className="car-expire">
                          Срок до: {formatBackendDate(car.rentExpiredAt, 'Нет данных')}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="cars-count">
                    {driverData?.cars?.length === 1
                      ? `${driverData?.cars?.length} автомобиль`
                      : driverData?.cars?.length > 1 && driverData?.cars?.length < 5
                        ? `${driverData?.cars?.length} автомобиля`
                        : `${driverData?.cars?.length || 0} автомобилей`}
                  </span>
                )}
              </div>

              <div className="drivers-table-row__cell drivers-table-row__cell--phone">
                {formatPhone(driverData.profile.phone)}
              </div>

              <div className="drivers-table-row__cell drivers-table-row__cell--action">
                {user?.role === 'autopark_employee' && (
                  <button className="drivers-table-edit-button" onClick={() => handleEditDriver(driverData)}>
                    Изменить
                  </button>
                )}
                <button
                  className="delete-button"
                  onClick={() => handleDeleteDriver(driverData.driver.id, driverData.profile.id)}
                >
                  Удалить
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Пагинация */}
      {totalPages > 1 && (
        <div className="drivers-pagination">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
        </div>
      )}

      {/* Модальное окно подтверждения удаления */}
      <ConfirmationModal
        isOpen={isModalOpen}
        title="Вы точно хотите удалить этого водителя?"
        onConfirm={confirmDeleteDriver}
        onCancel={cancelDeleteDriver}
      />

      {/* Модальное окно редактирования водителя */}
      <EditDriverModal
        isOpen={isEditModalOpen}
        drivers={drivers}
        driverToEdit={driverToEdit?.driver.id || ''}
        onClose={handleCloseEditModal}
        onSuccess={handleEditSuccess}
        driverData={driverToEdit || ({} as IAutoparkDriverWithProfileAndCars)}
      />

      {/* Модальное окно добавления водителя */}
      <AddDriverModal
        isOpen={isAddDriverModalOpen}
        onClose={handleCloseAddDriverModal}
        onSuccess={handleAddDriverSuccess}
        autoparkId={autoparkId}
      />
    </div>
  );
}

export default AutoparkDrivers;
